/**
 * adsScope — the drill scope every PPC level shares (PR 3, B1/B3; spec
 * _overhaul-spec/pr03-numbers.md §0.1 + B0).
 *
 * A drilled PPC page (Campaigns → Ad Groups → Targets → Search Terms, plus the
 * Product → Targets drill) shows ONE scope in its KPI strip, chart, day cards,
 * totals row and table. Before this module the scope lived in three places
 * that disagreed: the list fetch read it from the URL, the chart got only the
 * column-filter id list (so it drew the whole account), and Search Terms
 * folded it into filter chips with four URL writes in one tick, which
 * clobbered each other (react-router 7's functional `setSearchParams` hands
 * every call the RENDER-TIME params, so the last navigate wins).
 *
 * One vocabulary, used for the URL, the list endpoints, /api/timeseries and
 * the live tail (/api/timeseries/ams-tail):
 *
 *   scope field     URL (read)                 URL (written)   label key        API param
 *   portfolioId     portfolio_id               portfolio_id    portfolio_name   portfolio_id
 *   portfolioName   portfolio_name             portfolio_name  (it is the label) portfolio_name
 *   campaignId      campaign_id                campaign_id     campaign_name    campaign_id
 *   adGroupId       ad_group_id                ad_group_id     ad_group_name    ad_group_id
 *   targetId        keyword_id | target_id     keyword_id      keyword_label    target_id (search terms: keyword_id)
 *   asin            asin                       asin            —                asin
 *
 * `keyword_id` stays the WRITTEN key for a target because
 * ppc_search_terms.keyword_id holds keyword ids AND product/auto target ids
 * (the unified trigger id), and every existing link already uses it.
 *
 * Everything here except `useAdsScope` is pure and router-free, so it unit-
 * tests without a DOM (adsScope.test.ts). The Advertising hub (PR 4) builds
 * on this module: keep it pure, keep the vocabulary in this one place.
 */
import { useCallback, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TabActiveContext } from '../tabs/useTabVisible'

/** The drill scope: portfolio › campaign › ad group › target, plus a product.
 *  An absent field means "not narrowed by it". */
export type AdsScope = {
  portfolioId?: string
  /** A portfolio drilled by NAME (the Portfolio cell link). Only a scope field
   *  when there is no `portfolioId`; with an id the name is the id's label. */
  portfolioName?: string
  campaignId?: string
  adGroupId?: string
  /** A keyword id or a product/auto target id (the unified trigger id). */
  targetId?: string
  asin?: string
}

/** Human names carried beside the ids so a chip can read "Ad group: Blue"
 *  instead of a 15-digit id. Display only — never sent to an endpoint. */
export type AdsScopeLabels = {
  campaignName?: string
  adGroupName?: string
  targetLabel?: string
  /** The portfolio's name when the scope holds its id. */
  portfolioName?: string
}

export type AdsScopeField = keyof AdsScope

/** Every scope field, broadest first — the chip order. */
export const ADS_SCOPE_FIELDS: readonly AdsScopeField[] = [
  'portfolioId', 'portfolioName', 'campaignId', 'adGroupId', 'targetId', 'asin',
]

/** The URL key each field is WRITTEN under. */
const URL_KEY: Record<AdsScopeField, string> = {
  portfolioId: 'portfolio_id',
  portfolioName: 'portfolio_name',
  campaignId: 'campaign_id',
  adGroupId: 'ad_group_id',
  targetId: 'keyword_id',
  asin: 'asin',
}

/** Read-only alias: a target id may arrive as `target_id` (what the API
 *  calls it); it is always written back as `keyword_id`. */
const TARGET_ALIAS_KEY = 'target_id'

/** The label key carried beside an id field (portfolioName is its own label). */
const LABEL_URL_KEY: Partial<Record<AdsScopeField, string>> = {
  portfolioId: 'portfolio_name',
  campaignId: 'campaign_name',
  adGroupId: 'ad_group_name',
  targetId: 'keyword_label',
}

/** Which `AdsScopeLabels` entry labels each field. */
const LABEL_FIELD: Partial<Record<AdsScopeField, keyof AdsScopeLabels>> = {
  portfolioId: 'portfolioName',
  campaignId: 'campaignName',
  adGroupId: 'adGroupName',
  targetId: 'targetLabel',
}

/** Every URL key this module reads, including the `target_id` alias and the
 *  label keys. Anything else on the URL (a column filter `<table>_f`, `tab`,
 *  `include_inactive`, …) is never touched by a scope write. */
export const ADS_SCOPE_URL_KEYS: readonly string[] = [
  'portfolio_id', 'portfolio_name', 'campaign_id', 'campaign_name',
  'ad_group_id', 'ad_group_name', 'keyword_id', TARGET_ALIAS_KEY, 'keyword_label', 'asin',
]

/** Chip prefix per field (the existing chip copy: "Ad group: …", "Target: …"). */
const CHIP_PREFIX: Record<AdsScopeField, string> = {
  portfolioId: 'Portfolio',
  portfolioName: 'Portfolio',
  campaignId: 'Campaign',
  adGroupId: 'Ad group',
  targetId: 'Target',
  asin: 'ASIN',
}

/** The URL key a field is written under (the chip / incoming-filter key). */
export function scopeUrlKey(field: AdsScopeField): string {
  return URL_KEY[field]
}

/** The field a URL key belongs to, or null for a key that is not a scope id
 *  (label keys and non-scope params). `target_id` maps to `targetId`. */
export function scopeFieldOfUrlKey(key: string): AdsScopeField | null {
  if (key === TARGET_ALIAS_KEY) return 'targetId'
  for (const f of ADS_SCOPE_FIELDS) if (URL_KEY[f] === key) return f
  return null
}

/** Drop absent and empty values, so two scopes that mean the same thing are
 *  the same object shape (query keys, equality, `isScoped`). */
function clean<T extends Record<string, string | undefined>>(o: T): T {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(o)) if (v) out[k] = v
  return out as T
}

/** Read the scope and its labels from a URL. A portfolio name next to a
 *  portfolio id is the id's LABEL, not a second filter. */
export function readAdsScope(params: URLSearchParams): { scope: AdsScope; labels: AdsScopeLabels } {
  const get = (k: string) => params.get(k) || undefined
  const portfolioId = get('portfolio_id')
  const scope = clean<AdsScope>({
    portfolioId,
    portfolioName: portfolioId ? undefined : get('portfolio_name'),
    campaignId: get('campaign_id'),
    adGroupId: get('ad_group_id'),
    targetId: get('keyword_id') ?? get(TARGET_ALIAS_KEY),
    asin: get('asin'),
  })
  const labels = clean<AdsScopeLabels>({
    portfolioName: portfolioId ? get('portfolio_name') : undefined,
    campaignName: scope.campaignId ? get('campaign_name') : undefined,
    adGroupName: scope.adGroupId ? get('ad_group_name') : undefined,
    targetLabel: scope.targetId ? get('keyword_label') : undefined,
  })
  return { scope, labels }
}

/**
 * Write `scope` onto a COPY of `params` and return it (the input is never
 * mutated). Every scope key is rewritten: a field absent from `scope` is
 * deleted together with its label; `targetId` is written as `keyword_id` and
 * the `target_id` alias is dropped. Non-scope params are kept as they are.
 *
 * Labels: a label given in `labels` is written; otherwise the label already
 * on the URL is kept only while its id is unchanged (a new id with an old
 * name would put the wrong name on the chip), else it is dropped.
 */
export function writeAdsScope(
  params: URLSearchParams, scope: AdsScope, labels?: AdsScopeLabels,
): URLSearchParams {
  const prev = readAdsScope(params)
  const next = new URLSearchParams(params)
  for (const k of ADS_SCOPE_URL_KEYS) next.delete(k)
  const s = clean(scope)
  // An id wins over a name: with both, the name is written as the id's label.
  const portfolioLabel = labels?.portfolioName
    ?? (s.portfolioId && s.portfolioName ? s.portfolioName : undefined)
  for (const field of ADS_SCOPE_FIELDS) {
    if (field === 'portfolioName' && s.portfolioId) continue
    const value = s[field]
    if (!value) continue
    next.set(URL_KEY[field], value)
    const labelKey = LABEL_URL_KEY[field]
    const labelField = LABEL_FIELD[field]
    if (!labelKey || !labelField) continue
    const given = field === 'portfolioId' ? portfolioLabel : labels?.[labelField]
    const kept = prev.scope[field] === value ? prev.labels[labelField] : undefined
    const label = given ?? kept
    if (label) next.set(labelKey, label)
  }
  return next
}

/** A drill link: `path` with `scope` — and its labels — as the query string,
 *  in the URL vocabulary above. Pass the row's NAME as the label: the
 *  destination's chip then reads "Ad group: Blue" instead of a 15-digit id
 *  (#113: names, not ids an operator cannot search in Seller Central). */
export function scopeHref(path: string, scope: AdsScope, labels?: AdsScopeLabels): string {
  const qs = writeAdsScope(new URLSearchParams(), scope, labels).toString()
  return qs ? `${path}?${qs}` : path
}

/** The scope as list / timeseries / live-tail params. Absent fields are
 *  omitted (not `undefined`), so the object is safe to spread into a query
 *  key. A portfolio id wins over a name. */
export type ScopeApiParams = {
  portfolio_id?: string; portfolio_name?: string; campaign_id?: string
  ad_group_id?: string; target_id?: string; asin?: string
}
export function scopeApiParams(scope: AdsScope): ScopeApiParams {
  return clean<ScopeApiParams>({
    portfolio_id: scope.portfolioId,
    portfolio_name: scope.portfolioId ? undefined : scope.portfolioName,
    campaign_id: scope.campaignId,
    ad_group_id: scope.adGroupId,
    target_id: scope.targetId,
    asin: scope.asin,
  })
}

/** The scope as /api/search-terms params — the ONE place a target id is sent
 *  as `keyword_id` (that endpoint filters the raw ppc_search_terms.keyword_id,
 *  the trigger id). The endpoint has no asin or portfolio filter, so neither
 *  is sent: a page must not offer a scope its list cannot honour. */
export type SearchTermsScopeParams = { campaign_id?: string; ad_group_id?: string; keyword_id?: string }
export function searchTermsScopeParams(scope: AdsScope): SearchTermsScopeParams {
  return clean<SearchTermsScopeParams>({
    campaign_id: scope.campaignId,
    ad_group_id: scope.adGroupId,
    keyword_id: scope.targetId,
  })
}

/** A stable, order-independent string for a scope — for query keys and
 *  "did the scope change" checks. Two scopes with the same fields give the
 *  same key whatever order they were built in. */
export function scopeKey(scope: AdsScope): string {
  const s = clean(scope)
  return ADS_SCOPE_FIELDS.filter(f => s[f]).map(f => `${f}=${s[f]}`).join('&')
}

/** True when any scope field is set. */
export function isScoped(scope: AdsScope): boolean {
  return ADS_SCOPE_FIELDS.some(f => !!scope[f])
}

/** Keep only the fields a page honours. A page passes the fields its LIST
 *  endpoint can narrow by, so the chart and the day cards can never narrow by
 *  something the table beside them ignores. */
export function pickScope(scope: AdsScope, fields: readonly AdsScopeField[]): AdsScope {
  const out: AdsScope = {}
  for (const f of fields) if (scope[f]) out[f] = scope[f]
  return out
}

/** One removable chip per scope field, broadest first. The label falls back
 *  to the id when no name rode along. */
export type ScopeChip = { key: AdsScopeField; prefix: string; label: string }
export function scopeChips(scope: AdsScope, labels: AdsScopeLabels): ScopeChip[] {
  const out: ScopeChip[] = []
  for (const field of ADS_SCOPE_FIELDS) {
    const value = scope[field]
    if (!value) continue
    const labelField = LABEL_FIELD[field]
    const label = (labelField && labels[labelField]) || value
    out.push({ key: field, prefix: CHIP_PREFIX[field], label })
  }
  return out
}

/** A chart / day-card filter: the page's scope plus the chart's own filters
 *  (the column-filter id-list mirror, a match-type select, …). Empty values
 *  are dropped, so `withScope({}, undefined)` is `{}` and a chart with no
 *  narrowing keeps the same query key it always had. A chart filter that
 *  names a scope field wins over the scope (it is the narrower question). */
export function withScope<T extends object>(
  scope: AdsScope, filters?: T,
): T & ScopeApiParams {
  const out: Record<string, unknown> = { ...scopeApiParams(scope) }
  for (const [k, v] of Object.entries(filters ?? {})) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out as T & ScopeApiParams
}

/** The id-list params a chart mirror sends (the table's column-filter set). */
const ID_LIST_PARAMS: ReadonlySet<string> = new Set([
  'campaign_id_in', 'asin_in', 'ad_group_id_in', 'keyword_id_in',
])
const SCOPE_API_PARAMS: ReadonlySet<string> = new Set([
  'portfolio_id', 'portfolio_name', 'campaign_id', 'ad_group_id', 'target_id', 'asin',
])

/** The two scalar filters the live tail applies itself (PR 12d). */
const TAIL_SCALAR_PARAMS: ReadonlySet<string> = new Set(['ad_type', 'marketplace'])

/** True when every set filter is one the live tail (/api/timeseries/ams-tail)
 *  can apply on every leg: a scope param, an id list, or the ad type /
 *  marketplace selects. Match type, expression type and placement are
 *  report-only: asking for the tail there would draw a today/yesterday point on
 *  a different basis from the days before it, so the chart and the day cards
 *  skip it. A marketplace other than US gets an honest empty tail (the stream
 *  is subscribed for the US advertiser only). */
export function tailCanApply(filters?: object): boolean {
  for (const [k, v] of Object.entries(filters ?? {})) {
    if (v === undefined || v === null || v === '') continue
    if (!SCOPE_API_PARAMS.has(k) && !ID_LIST_PARAMS.has(k) && !TAIL_SCALAR_PARAMS.has(k)) return false
  }
  return true
}

/** True when the filters carry a column-filter id list (a cohort picked from
 *  the table's rows, as opposed to a drill scope). */
export function hasIdListFilter(filters?: object): boolean {
  return Object.entries(filters ?? {}).some(([k, v]) => ID_LIST_PARAMS.has(k) && !!v)
}

/** The scope with one field removed. */
export function scopeWithout(scope: AdsScope, field: AdsScopeField): AdsScope {
  const out = { ...scope }
  delete out[field]
  // Clearing the portfolio clears it however it was drilled.
  if (field === 'portfolioId' || field === 'portfolioName') {
    delete out.portfolioId
    delete out.portfolioName
  }
  return out
}

/** What a chip's X writes: the URL with one scope field and its label
 *  removed; every other field, its label and every non-scope param kept.
 *  `useAdsScope().clear` is exactly this, in one `setSearchParams` call. */
export function clearScopeField(params: URLSearchParams, field: AdsScopeField): URLSearchParams {
  const cur = readAdsScope(params)
  return writeAdsScope(params, scopeWithout(cur.scope, field), cur.labels)
}

export type AdsScopeState = {
  /** The page's scope (narrowed to `fields` when the page passed them). */
  scope: AdsScope
  labels: AdsScopeLabels
  /** Replace the WHOLE scope (fields absent from `next` are cleared) in one
   *  URL write. `push` adds a history entry (a drill); default replaces. */
  setScope: (next: AdsScope, labels?: AdsScopeLabels, opts?: { push?: boolean }) => void
  /** Remove one field (and its label) in one URL write. */
  clear: (field: AdsScopeField) => void
  /** Remove every scope field in one URL write. */
  clearAll: () => void
}

/**
 * The page's drill scope, read from the URL; writes go back to the URL with
 * ONE `setSearchParams` call per user action (two calls in one tick
 * overwrite each other — the Search Terms clobber, B3).
 *
 * `fields` narrows what the page honours (see `pickScope`); the writers
 * always operate on the full URL scope, so clearing one chip never drops a
 * field the page merely ignores.
 *
 * Keep-alive tabs: an inactive tab is rendered against its frozen location
 * but `setSearchParams` writes the ONE global router, so an unguarded write
 * from a hidden tab would yank the app back to that tab's URL. Every writer
 * is a no-op unless this is the active tab pill (TabActiveContext defaults
 * to true outside the tab system, so standalone pages and tests write).
 */
export function useAdsScope(fields?: readonly AdsScopeField[]): AdsScopeState {
  const [params, setParams] = useSearchParams()
  const isActivePill = useContext(TabActiveContext)
  const read = useMemo(() => readAdsScope(params), [params])
  const fieldsKey = fields ? fields.join(',') : ''
  const scope = useMemo(
    () => (fieldsKey ? pickScope(read.scope, fieldsKey.split(',') as AdsScopeField[]) : read.scope),
    [read, fieldsKey],
  )

  const setScope = useCallback((next: AdsScope, labels?: AdsScopeLabels, opts?: { push?: boolean }) => {
    if (!isActivePill) return
    setParams(prev => writeAdsScope(prev, next, labels), { replace: !opts?.push })
  }, [setParams, isActivePill])

  const clear = useCallback((field: AdsScopeField) => {
    if (!isActivePill) return
    setParams(prev => clearScopeField(prev, field), { replace: true })
  }, [setParams, isActivePill])

  const clearAll = useCallback(() => {
    if (!isActivePill) return
    setParams(prev => writeAdsScope(prev, {}), { replace: true })
  }, [setParams, isActivePill])

  return { scope, labels: read.labels, setScope, clear, clearAll }
}
